import React, { useState, useCallback, memo } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Save, Send, UserX } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { useTeacherProfile } from "@/contexts/TeacherProfileContext";
import {
  useSessionDetail, useMarkAttendance, useUpsertNotes,
  useUpsertSummary, useSendSummary,
} from "@/hooks/use-class-management";
import { AttendanceToggle } from "@/components/teacher/classes/AttendanceToggle";
import { SendStatusBadge } from "@/components/teacher/classes/SendStatusBadge";
import { ScheduleWarningBanner } from "@/components/teacher/classes/ScheduleWarningBanner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { ScheduleWarning, SummarySendResult } from "@/services/classManagementService";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatSessionTitle(iso: string): string {
  return new Date(iso).toLocaleDateString([], {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

function formatSessionTime(iso: string, durationMinutes: number): string {
  const start = new Date(iso);
  const end   = new Date(start.getTime() + durationMinutes * 60_000);
  const fmt   = (d: Date) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${fmt(start)} – ${fmt(end)}`;
}

// ── Attendance summary pills ──────────────────────────────────────────────────

function AttendanceSummaryBadge({
  attendance,
}: {
  attendance: Array<{ status: string | null }>;
}) {
  const present  = attendance.filter((a) => a.status === "PRESENT").length;
  const late     = attendance.filter((a) => a.status === "LATE").length;
  const absent   = attendance.filter((a) => a.status === "ABSENT").length;
  const unmarked = attendance.filter((a) => !a.status).length;

  if (attendance.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {present > 0 && (
        <span className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
          <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> {present} present
        </span>
      )}
      {late > 0 && (
        <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
          <Clock className="w-3 h-3" aria-hidden="true" /> {late} late
        </span>
      )}
      {absent > 0 && (
        <span className="inline-flex items-center gap-1 text-xs font-semibold bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full">
          <UserX className="w-3 h-3" aria-hidden="true" /> {absent} absent
        </span>
      )}
      {unmarked > 0 && (
        <span className="text-xs text-slate-400">{unmarked} not marked</span>
      )}
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────
// Defined OUTSIDE the page component so React sees a stable component identity
// across re-renders. Defining it inside the component body causes every keystroke
// to create a new component type, which unmounts and remounts the textareas.

interface PageShellProps {
  offeringId: string;
  teacherName: string;
  teacherAvatar: string;
  teacherSubjects: string[];
  sessionIso?: string;
  sessionDurationMinutes?: number;
  children: React.ReactNode;
  busy?: boolean;
}

const PageShell = memo(function PageShell({
  offeringId,
  teacherName,
  teacherAvatar,
  teacherSubjects,
  sessionIso,
  sessionDurationMinutes,
  children,
  busy = false,
}: PageShellProps) {
  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={teacherName}
            teacherAvatar={teacherAvatar}
            subjects={teacherSubjects}
          />
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 bg-white">
              <Link
                to={`/teacher-classes/${offeringId}`}
                className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 transition-colors duration-150 mb-2"
              >
                <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
                Back to class
              </Link>
              <h1 className="text-2xl font-bold text-slate-900">
                {sessionIso ? formatSessionTitle(sessionIso) : "Session Details"}
              </h1>
              {sessionIso && sessionDurationMinutes != null && (
                <p className="text-sm text-slate-500 mt-0.5">
                  {formatSessionTime(sessionIso, sessionDurationMinutes)}
                  <span className="mx-2 text-slate-300" aria-hidden="true">·</span>
                  {sessionDurationMinutes} min
                </p>
              )}
            </div>
            <main className="flex-1 overflow-auto bg-slate-50" aria-busy={busy}>
              <div className="px-4 sm:px-6 py-6 max-w-3xl space-y-5">
                {children}
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    </div>
  );
});

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SessionDetailPage() {
  usePageTitle("Session Details | BookMyCoaching");

  const { offeringId = "", sessionId = "" } = useParams<{
    offeringId: string;
    sessionId: string;
  }>();

  const { teacherProfile } = useTeacherProfile();
  const teacherName     = teacherProfile?.fullName     || "";
  const teacherAvatar   = teacherProfile?.profileImage || "";
  const teacherSubjects: string[] = (teacherProfile?.subjects || [])
    .map((s: any) => typeof s === "string" ? s : s?.subjectName || s?.subject?.name || s?.name || "")
    .filter(Boolean);

  const { data, isLoading, isError, refetch } = useSessionDetail(sessionId);
  const markAttendanceMutation = useMarkAttendance();
  const upsertNotesMutation    = useUpsertNotes();
  const upsertSummaryMutation  = useUpsertSummary();
  const sendSummaryMutation    = useSendSummary();

  const [attendanceOverrides, setAttendanceOverrides] = useState<
    Record<string, "PRESENT" | "ABSENT" | "LATE">
  >({});
  const [notesBody,    setNotesBody]    = useState<string | null>(null);
  const [summaryBody,  setSummaryBody]  = useState<string | null>(null);
  const [sendResult,   setSendResult]   = useState<SummarySendResult | null>(null);
  const [notesSaved,   setNotesSaved]   = useState(false);
  const [summarySaved, setSummarySaved] = useState(false);

  const handleAttendanceChange = useCallback(
    (enrollmentId: string, status: "PRESENT" | "ABSENT" | "LATE") => {
      setAttendanceOverrides((prev) => ({ ...prev, [enrollmentId]: status }));
      markAttendanceMutation.mutate({ sessionId, enrollmentId, status });
    },
    [sessionId, markAttendanceMutation],
  );

  async function handleSaveNotes(notes: string) {
    try {
      await upsertNotesMutation.mutateAsync({ sessionId, body: notes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch {
      toast({ title: "Failed to save notes.", variant: "destructive" });
    }
  }

  async function handleSaveSummary(summary: string) {
    try {
      await upsertSummaryMutation.mutateAsync({ sessionId, body: summary });
      setSummarySaved(true);
      setTimeout(() => setSummarySaved(false), 2000);
    } catch {
      toast({ title: "Failed to save summary.", variant: "destructive" });
    }
  }

  async function handleSendSummary(summary: string) {
    try {
      // Always save the draft first so the backend has content to send.
      await upsertSummaryMutation.mutateAsync({ sessionId, body: summary });
      const result = await sendSummaryMutation.mutateAsync(sessionId);
      setSendResult(result);
      setSummarySaved(false);
      toast({ title: `Summary sent to ${result.sentCount} student${result.sentCount !== 1 ? "s" : ""}.` });
    } catch {
      toast({ title: "Failed to send summary.", variant: "destructive" });
    }
  }

  const shellProps = {
    offeringId,
    teacherName,
    teacherAvatar,
    teacherSubjects,
    sessionIso: data?.session.scheduledStart,
    sessionDurationMinutes: data?.session.durationMinutes,
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <PageShell {...shellProps} busy>
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </PageShell>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (isError || !data) {
    return (
      <PageShell {...shellProps}>
        <div className="flex flex-col items-center justify-center text-center py-16 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-700 mb-4">Could not load session details.</p>
          <Button variant="outline" className="cursor-pointer" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      </PageShell>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const scheduleWarning = data.scheduleWarning as unknown as (ScheduleWarning | string | null);
  const enrollments     = data.attendance ?? [];
  const attendanceWithOverrides = enrollments.map((e) => ({
    ...e,
    status: attendanceOverrides[e.enrollmentId] ?? e.status ?? null,
  }));
  const currentNotes   = notesBody   ?? data.notes   ?? "";
  const currentSummary = summaryBody ?? data.summary  ?? "";
  const alreadySent    = sendResult !== null || data.summarySentAt != null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageShell {...shellProps}>
      {/* Schedule warning */}
      {scheduleWarning && <ScheduleWarningBanner warning={scheduleWarning} />}

      {/* ── Attendance ── */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-bold text-slate-800">Attendance</h2>
          <AttendanceSummaryBadge attendance={attendanceWithOverrides} />
        </div>
        <div className="divide-y divide-slate-100">
          {enrollments.length === 0 ? (
            <p className="text-sm text-slate-500 px-5 py-8 text-center">No enrolled students.</p>
          ) : (
            enrollments.map((e) => {
              const current = attendanceOverrides[e.enrollmentId] ?? e.status ?? null;
              return (
                <div
                  key={e.enrollmentId}
                  className={cn(
                    "flex items-center gap-4 px-5 py-3.5 transition-colors duration-150",
                    current === "PRESENT" && "bg-emerald-50/40",
                    current === "LATE"    && "bg-amber-50/40",
                    current === "ABSENT"  && "bg-red-50/30",
                  )}
                >
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-slate-500" aria-hidden="true">
                      {(e.studentName || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-slate-800 flex-1 min-w-0 truncate">
                    {e.studentName}
                  </span>
                  <AttendanceToggle
                    enrollmentId={e.enrollmentId}
                    studentName={e.studentName}
                    current={current}
                    onChange={(status) => handleAttendanceChange(e.enrollmentId, status)}
                    disabled={markAttendanceMutation.isPending}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Session notes (private) ── */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-800">Session notes</h2>
          <p className="text-xs text-slate-400 mt-0.5">Private — only visible to you</p>
        </div>
        <div className="p-5 space-y-3">
          <Textarea
            aria-label="Notes (private)"
            value={currentNotes}
            onChange={(e) => { setNotesBody(e.target.value); setNotesSaved(false); }}
            rows={4}
            className="resize-y border-slate-200 focus-visible:ring-indigo-500 text-sm"
            placeholder="Add private notes about this session…"
          />
          <Button
            size="sm"
            variant="outline"
            className={cn(
              "cursor-pointer transition-colors duration-150 gap-1.5",
              notesSaved && "border-emerald-300 text-emerald-700 bg-emerald-50",
            )}
            disabled={upsertNotesMutation.isPending}
            onClick={() => void handleSaveNotes(currentNotes)}
          >
            {upsertNotesMutation.isPending ? (
              <><Save className="h-3.5 w-3.5 animate-pulse" aria-hidden="true" /> Saving…</>
            ) : notesSaved ? (
              <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" /> Saved</>
            ) : (
              <><Save className="h-3.5 w-3.5" aria-hidden="true" /> Save notes</>
            )}
          </Button>
        </div>
      </div>

      {/* ── Session summary (shared with students) ── */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-slate-800">Session summary</h2>
            <p className="text-xs text-slate-400 mt-0.5">Shared with enrolled students when sent</p>
          </div>
          {alreadySent && (
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 text-xs shrink-0">
              Sent
            </Badge>
          )}
        </div>
        <div className="p-5 space-y-3">
          <Textarea
            aria-label="Summary (shared with students)"
            value={currentSummary}
            onChange={(e) => { setSummaryBody(e.target.value); setSummarySaved(false); }}
            rows={5}
            className="resize-y border-slate-200 focus-visible:ring-indigo-500 text-sm"
            placeholder="Write a summary of what was covered in this session…"
          />
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "cursor-pointer transition-colors duration-150 gap-1.5",
                summarySaved && "border-emerald-300 text-emerald-700 bg-emerald-50",
              )}
              disabled={upsertSummaryMutation.isPending}
              onClick={() => void handleSaveSummary(currentSummary)}
            >
              {upsertSummaryMutation.isPending ? (
                <><Save className="h-3.5 w-3.5 animate-pulse" aria-hidden="true" /> Saving…</>
              ) : summarySaved ? (
                <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" /> Saved</>
              ) : (
                <><Save className="h-3.5 w-3.5" aria-hidden="true" /> Save</>
              )}
            </Button>
            <Button
              size="sm"
              className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-150 gap-1.5"
              disabled={sendSummaryMutation.isPending || !currentSummary.trim()}
              onClick={() => void handleSendSummary(currentSummary)}
            >
              {sendSummaryMutation.isPending ? (
                <><Send className="h-3.5 w-3.5 animate-pulse" aria-hidden="true" /> Sending…</>
              ) : (
                <><Send className="h-3.5 w-3.5" aria-hidden="true" /> {alreadySent ? "Resend summary" : "Send summary"}</>
              )}
            </Button>
          </div>

          {(sendResult !== null || data.summarySentAt != null) && (
            <SendStatusBadge
              sentCount={sendResult?.sentCount ?? data.summarySentCount ?? 0}
              skippedCount={sendResult?.skippedCount ?? data.summarySkippedCount ?? 0}
              sentAt={sendResult?.sentAt ?? data.summarySentAt ?? ""}
            />
          )}
        </div>
      </div>
    </PageShell>
  );
}
